<template>
  <div>
    <div class="mb-3">
      <div class="relative">
        <UInput
          v-model="query"
          :disabled="props.disabled"
          :loading="loading"
          placeholder="Find address..."
          trailing
          trailing-icon="i-heroicons-map-pin"
          size="lg"
          class="z-20 mb-3"
          autocomplete="off"
          @focus="showSuggestions = true"
          @blur="hideSuggestions"
        />

        <div
          v-if="showSuggestions && query.trim().length >= 3"
          class="absolute inset-x-0 top-full z-30 -mt-3 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
        >
          <div v-if="loading" class="px-3 py-2 text-sm text-gray-500">
            Searching addresses...
          </div>

          <template v-else-if="addresses.length">
            <button
              v-for="(address, index) in addresses"
              :key="`${address.formattedAddress}-${index}`"
              type="button"
              class="block w-full px-3 py-2 text-left text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
              @mousedown.prevent="selectAddress(address)"
            >
              {{ address.formattedAddress }}
            </button>
          </template>

          <div v-else class="px-3 py-2 text-sm text-gray-500">
            No addresses found.
          </div>
        </div>

        <div id="address-autocomplete-tag" />
      </div>

      <UInput v-model="model.address2" :disabled="props.disabled" size="lg" />
    </div>

    <div class="sm:flex sm:items-start sm:space-x-3  rtl:space-x-reverse mb-3">
      <UFormGroup :label="$t('general.suburb')" :required="requiredFields?.suburb" class="w-full sm:w-1/2">
        <UInput v-model="model.suburb" :disabled="props.disabled" size="lg" />
      </UFormGroup>

      <UFormGroup :label="$t('general.city')" :required="requiredFields?.city" class="w-full sm:w-1/2">
        <UInput v-model="model.city" :disabled="props.disabled" size="lg" />
      </UFormGroup>
    </div>

    <div class="sm:flex sm:items-start sm:space-x-3  rtl:space-x-reverse mb-3">
      <UFormGroup :label="$t('general.region')" :required="requiredFields?.region" class="w-full sm:w-1/2">
        <UInput v-model="model.region" :disabled="props.disabled" size="lg" />
      </UFormGroup>

      <UFormGroup :label="$t('general.postcode')" :required="requiredFields?.postcode" class="w-full sm:w-1/2">
        <UInput v-model="model.postcode" :disabled="props.disabled" size="lg" />
      </UFormGroup>
    </div>

    <div class="sm:flex sm:items-start sm:space-x-3  rtl:space-x-reverse mb-8">
      <UFormGroup :label="$t('general.country')" :required="requiredFields?.country" class="w-full sm:w-1/2">
        <UInput v-model="model.country" :disabled="props.disabled" size="lg" />
      </UFormGroup>
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'BAddress'
}
</script>

<script setup lang="ts">
import Radar from 'radar-sdk-js';

import type { ModelRef } from 'vue';
import type { User } from '@@/types/user';

const radar_test_key = 'prj_test_pk_7d0b8d3107d27df9fb890c5141faeb27f218a146'
const radar_live_key = 'prj_live_pk_352457a5eebd2c0766f55e0d63071d96c36a082e'

Radar.initialize(process.env.NODE_ENV === 'development' ? radar_test_key : radar_live_key);

const model: ModelRef<User | undefined> = defineModel({
  required: true
})

const props = defineProps<{
  disabled: boolean
  requiredFields?: Record<string, boolean>
}>()

type Address = Awaited<ReturnType<typeof Radar.autocomplete>>['addresses'][number] & {
  dependentLocality?: string
}

const loading = ref(false)
const query = ref(model.value?.address || '')
const addresses = ref<Address[]>([])
const showSuggestions = ref(false)

let searchTimeout: ReturnType<typeof setTimeout> | undefined
let requestId = 0
let selectedAddress: Address | undefined

watch(query, (value) => {
  clearTimeout(searchTimeout)

  const currentRequestId = ++requestId
  const searchQuery = value.trim()
  const selectedQuery = selectedAddress?.formattedAddress || selectedAddress?.addressLabel || ''

  if (selectedAddress && value === selectedQuery) {
    addresses.value = []
    loading.value = false
    return
  }

  selectedAddress = undefined

  if (model.value) {
    model.value.address = value
  }

  if (searchQuery.length < 3) {
    addresses.value = []
    loading.value = false
    return
  }

  addresses.value = []
  loading.value = true

  searchTimeout = setTimeout(async () => {
    try {
      const result = await Radar.autocomplete({
        query: searchQuery,
        limit: 5
      })

      if (currentRequestId === requestId) {
        addresses.value = result.addresses
      }
    } catch {
      if (currentRequestId === requestId) {
        addresses.value = []
      }
    } finally {
      if (currentRequestId === requestId) {
        loading.value = false
      }
    }
  }, 500)
})

function hideSuggestions () {
  window.setTimeout(() => {
    showSuggestions.value = false
  }, 100)
}

function selectAddress (address: Address) {
  if (model.value) {
    model.value.address = address.addressLabel || address.formattedAddress || ''
    model.value.address2 = ''
    model.value.suburb = address.borough || address.dependentLocality || ''
    model.value.city = address.city || ''
    model.value.region = address.state || ''
    model.value.postcode = address.postalCode || ''
    model.value.country = address.country || ''
  }

  selectedAddress = address
  query.value = address.formattedAddress || address.addressLabel || ''
  showSuggestions.value = false
}

onBeforeUnmount(() => {
  clearTimeout(searchTimeout)
})
</script>
